import React, { useEffect, useMemo, useState } from 'react';
import { limit, onSnapshot, orderBy, query, where } from 'firebase/firestore';
import { activityFeedColl, dailyAnswerDoc, moodsDoc, nightNotesColl, petDoc } from '../db';
import { Screen, UserProfile } from '../App';
import PetCharacter, { DEFAULT_CONFIG, CharacterConfig } from './PetCharacter';
import type { ActivityFeedItem } from '../utils/activityFeed';
import { buildWeeklySummary, getWeeklyQuest, getWeeklyQuestProgress, MoodData } from '../utils/relationshipInsights';

interface Props {
  user: UserProfile;
  navigate: (s: Screen) => void;
}

interface DayData { [uid: string]: { answer: string; name: string }; }
interface NightNoteMini { text: string; userId: string; userName: string; date: string; timestamp?: any; }
interface PetMiniState { lastCareAt?: any; lastBondAt?: any; characterConfig?: CharacterConfig; bond?: number; }

function getTodayKey() {
  const d = new Date();
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
}

function timestampToDateKey(timestamp: any) {
  const raw = timestamp?.toMillis?.()
    ?? (timestamp?.seconds ? timestamp.seconds * 1000 : timestamp instanceof Date ? timestamp.getTime() : 0);
  if (!raw) return '';
  const d = new Date(raw);
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
}

function stepIcon(screen: Screen) {
  if (screen === 'question') return '?';
  if (screen === 'mood') return '≋';
  if (screen === 'notes') return '✎';
  if (screen === 'pet') return '◇';
  if (screen === 'imhere') return '♡';
  return '•';
}

export default function DailyFlow({ user, navigate }: Props) {
  const [todayAnswers, setTodayAnswers] = useState<DayData>({});
  const [moods, setMoods] = useState<MoodData>({});
  const [todayNightNotes, setTodayNightNotes] = useState<NightNoteMini[]>([]);
  const [pet, setPet] = useState<PetMiniState | null>(null);
  const [activityFeed, setActivityFeed] = useState<ActivityFeedItem[]>([]);

  const today = getTodayKey();
  const fallbackPartner = user.displayName === 'Serhat' ? 'Ayça' : 'Serhat';

  useEffect(() => {
    return onSnapshot(dailyAnswerDoc(user.coupleId, today), snap => {
      setTodayAnswers(snap.exists() ? snap.data() as DayData : {});
    });
  }, [today, user.coupleId]);

  useEffect(() => {
    return onSnapshot(moodsDoc(user.coupleId), snap => {
      setMoods(snap.exists() ? snap.data() as MoodData : {});
    });
  }, [user.coupleId]);

  useEffect(() => {
    const q = query(nightNotesColl(user.coupleId), where('date', '==', today));
    return onSnapshot(q, snap => {
      setTodayNightNotes(snap.docs.map(d => d.data() as NightNoteMini));
    });
  }, [today, user.coupleId]);

  useEffect(() => {
    return onSnapshot(petDoc(user.coupleId), snap => {
      setPet(snap.exists() ? snap.data() as PetMiniState : null);
    });
  }, [user.coupleId]);

  useEffect(() => {
    const q = query(activityFeedColl(user.coupleId), orderBy('createdAt', 'desc'), limit(24));
    return onSnapshot(q, snap => {
      setActivityFeed(snap.docs.map(d => ({ id: d.id, ...d.data() } as ActivityFeedItem)));
    });
  }, [user.coupleId]);

  const todayMood = moods[today] || {};
  const myMood = todayMood[user.uid];
  const partnerMood = Object.entries(todayMood).find(([uid]) => uid !== user.uid)?.[1];
  const answeredToday = Boolean(todayAnswers[user.uid]);
  const partnerAnswer = Object.entries(todayAnswers).find(([uid]) => uid !== user.uid)?.[1];
  const hasNightNote = todayNightNotes.some(note => note.userId === user.uid);
  const partnerNightNote = todayNightNotes.find(note => note.userId !== user.uid);
  const partnerFeed = activityFeed.find(item => item.actorId !== user.uid && item.actorName);
  const partner = partnerAnswer?.name || partnerMood?.name || partnerNightNote?.userName || partnerFeed?.actorName || fallbackPartner;
  const petName = pet?.characterConfig?.name || DEFAULT_CONFIG.name;
  const feedToday = activityFeed.filter(item => item.dayKey === today);
  const petCareToday = timestampToDateKey(pet?.lastCareAt || pet?.lastBondAt) === today
    || feedToday.some(item => item.actorId === user.uid && (item.type === 'pet_care' || item.type === 'pet_ritual'));
  const sentSignalToday = feedToday.some(item => item.actorId === user.uid && item.type === 'heartbeat');

  const steps = useMemo(() => ([
    {
      done: answeredToday,
      label: 'Günün sorusu',
      detail: partnerAnswer && !answeredToday ? `${partner} yazdı, seninki bekleniyor.` : 'Bugünden bir cevap bırak.',
      screen: 'question' as Screen,
    },
    {
      done: Boolean(myMood),
      label: 'Bugünkü hal',
      detail: partnerMood && !myMood ? `${partner} halini seçti.` : 'İkinizin gün rengi yan yana gelsin.',
      screen: 'mood' as Screen,
    },
    {
      done: hasNightNote,
      label: 'Gece notu',
      detail: partnerNightNote && !hasNightNote ? `${partner} geceye bir cümle bıraktı.` : 'Bugünden kalacak tek cümle.',
      screen: 'notes' as Screen,
    },
    {
      done: petCareToday,
      label: `${petName} ilgisi`,
      detail: 'Bir bakım ya da günlük ritüel yeter.',
      screen: 'pet' as Screen,
    },
    {
      done: sentSignalToday,
      label: 'Bir işaret',
      detail: `${partner} için tek dokunuşluk bir haber.`,
      screen: 'imhere' as Screen,
    },
  ]), [answeredToday, hasNightNote, myMood, partner, partnerAnswer, partnerMood, partnerNightNote, petCareToday, petName, sentSignalToday]);

  const completed = steps.filter(step => step.done).length;
  const progress = Math.round((completed / steps.length) * 100);
  const nextStep = steps.find(step => !step.done);
  const quest = getWeeklyQuest();
  const questProgress = getWeeklyQuestProgress(activityFeed, quest);
  const weeklySummary = buildWeeklySummary(activityFeed, moods);

  return (
    <main className="daily-flow-screen">
      <header className="flow-header">
        <button className="back-btn" onClick={() => navigate('home')}>←</button>
        <div>
          <p>Bugün 2 dakika</p>
          <h1>{nextStep ? 'Ritmi birlikte tamamla.' : 'Bugün tamam.'}</h1>
          <span>{nextStep ? `${completed}/${steps.length} küçük iz tamam.` : `${petName} bugün ikinizden de iz aldı.`}</span>
        </div>
      </header>

      <section className="flow-hero">
        <div>
          <small>{nextStep ? nextStep.label : 'Gün yerini buldu'}</small>
          <h2>{nextStep ? nextStep.detail : 'Bugünün hafızası hazır.'}</h2>
          <p>{nextStep ? 'Tek tek aramana gerek yok; sıradaki en anlamlı adım burada.' : 'İstersen Anılar tarafında bugünden kalanlara bakabilirsin.'}</p>
        </div>
        <div className="today-progress" style={{ '--progress': `${progress}%` } as React.CSSProperties}>
          <span>{progress}</span>
          <small>%</small>
        </div>
        <button onClick={() => navigate(nextStep ? nextStep.screen : 'memories')}>
          {nextStep ? 'Sıradaki adım' : 'Anılara bak'}
        </button>
      </section>

      <section className="flow-steps">
        {steps.map(step => (
          <button key={step.label} className={step.done ? 'done' : ''} onClick={() => navigate(step.screen)}>
            <span>{step.done ? '✓' : stepIcon(step.screen)}</span>
            <div>
              <strong>{step.label}</strong>
              <small>{step.done ? 'Tamamlandı' : step.detail}</small>
            </div>
            <b>›</b>
          </button>
        ))}
      </section>

      <section className="flow-side-grid">
        <button className="weekly-quest-card" onClick={() => navigate(quest.screen as Screen)}>
          <small>Haftalık görev</small>
          <strong>{quest.title}</strong>
          <p>{quest.detail}</p>
          <div className="quest-bar"><span style={{ width: `${questProgress.percent}%` }} /></div>
          <em>{questProgress.done ? 'Tamamlandı' : `${questProgress.count}/${questProgress.target}`}</em>
        </button>

        <button className="weekly-summary-card" onClick={() => navigate('memories')}>
          <small>Haftalık özet</small>
          <strong>{weeklySummary.title}</strong>
          <p>{weeklySummary.detail}</p>
        </button>
      </section>

      <section className="flow-pet-strip" onClick={() => navigate('pet')}>
        <PetCharacter config={pet?.characterConfig || DEFAULT_CONFIG} size={68} mood={(pet?.bond || 0) > 70 ? 'happy' : 'normal'} />
        <div>
          <strong>{petName}</strong>
          <p>{petName}, günlük ritmi tamamladıkça daha fazla şey hatırlıyor.</p>
        </div>
      </section>
    </main>
  );
}
